import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";

const Home = lazy(() => import("@/pages/Home"));
const Profile = lazy(() => import("@/pages/Profile"));
const Verify = lazy(() => import("@/pages/Verify"));
const Admin = lazy(() => import("@/pages/Admin"));
const MyPlans = lazy(() => import("@/pages/MyPlans"));
const AdminPlan = lazy(() => import("@/pages/admin/AdminPlan"));

const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminCompany = lazy(() => import("@/pages/admin/AdminCompany"));
const AdminEmployees = lazy(() => import("@/pages/admin/AdminEmployees"));
const AdminPayroll = lazy(() => import("@/pages/admin/AdminPayroll"));
const AdminReports = lazy(() => import("@/pages/admin/AdminReports"));
const AdminIncentives = lazy(() => import("@/pages/admin/AdminIncentives"));
const AdminCompliance = lazy(() => import("@/pages/admin/AdminCompliance"));
const AdminBackups = lazy(() => import("@/pages/admin/AdminBackups"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport"));

const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const SuperAnalytics = lazy(() => import("@/pages/SuperAnalytics"));
const Login = lazy(() => import("@/pages/Login"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const ActiveTripDashboard = lazy(() => import("@/components/ActiveTripDashboard"));
const BusKiosk = lazy(() => import("@/components/BusKiosk"));
const KpiReport = lazy(() => import("@/pages/KpiReport"));
const AiAdvisor = lazy(() => import("@/pages/AiAdvisor"));
const VerifyAuth = lazy(() => import("@/pages/auth/VerifyAuth"));
const ConfirmDevice = lazy(() => import("@/pages/auth/ConfirmDevice"));
const SsoCallback = lazy(() => import("@/pages/auth/SsoCallback"));
const MfaChallenge = lazy(() => import("@/pages/auth/MfaChallenge"));
const MfaSetup = lazy(() => import("@/pages/auth/MfaSetup"));
const SignupPage = lazy(() => import("@/pages/signup/SignupPage"));
const SignupThanks = lazy(() => import("@/pages/signup/SignupThanks"));
const OnboardingWizard = lazy(() => import("@/pages/auth/OnboardingWizard"));
const LegalPolicy = lazy(() => import("@/pages/LegalPolicy"));
import InstallPrompt from "@/components/InstallPrompt";
import Layout from "@/Layout";
import { Toaster } from "@/components/ui/sonner";
import { UserProvider, useUser } from "@/context/UserContext";
import { TenantProvider } from "@/context/TenantContext";
import { I18nProvider } from "@/i18n/I18nContext";
import TopProgressBar from "@/components/TopProgressBar";
import PageLoader from "@/components/PageLoader";
import ErrorBoundary from "@/components/ErrorBoundary";
import { OfflineSyncProvider } from "@/context/OfflineSyncContext";
import { rememberReturnTo } from "@/lib/returnTo";

const PrivateRoute = ({ children }) => {
  const { user, initialized } = useUser();
  const location = useLocation();
  
  if (!initialized) return <PageLoader />;
  if (!user) {
    // Al entrar, de vuelta aquí (p. ej. el enlace al informe de KPI del email)
    rememberReturnTo(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  // Doble factor obligatorio y aún sin activar: la sesión solo sirve para eso
  if (user.mfa_setup_required && location.pathname !== "/auth/mfa-setup") {
    return <Navigate to="/auth/mfa-setup" replace />;
  }
  if (!user.mfa_setup_required && location.pathname === "/auth/mfa-setup") {
    return <Navigate to="/" replace />;
  }
  
  // Force onboarding if logged in but not onboarded
  if (user && user.is_onboarded === false && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  // Persona externa del plan de movilidad (p. ej. sindicato): solo el plan
  if (user.role === "pmst_guest" && location.pathname !== "/plan") {
    return <Navigate to="/plan" replace />;
  }
  
  return children;
};

// Al ir a otra pantalla, se empieza arriba (antes se heredaba el desplazamiento
// de la anterior: «Mi viaje» abierto desde el inicio bajado salía a media
// página). Al volver atrás, el navegador recupera donde se estaba. Cambiar de
// pestaña (?tab=) no cuenta: es la misma pantalla.
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  React.useEffect(() => {
    if (navigationType !== "POP") window.scrollTo(0, 0);
  }, [pathname, navigationType]);
  return null;
};

// /admin → /admin/dashboard conservando ?company= (si no, al entrar por el
// índice se perdía la empresa seleccionada).
const AdminIndexRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={{ pathname: "dashboard", search }} replace />;
};

function App() {
  return (
    <div className="App min-h-screen bg-[#F7FAF8]">
      <I18nProvider>
        <TopProgressBar />
        <OfflineSyncProvider>
          <UserProvider>
            <TenantProvider>
              <BrowserRouter>
                <ScrollToTop />
                <ErrorBoundary fullScreen>
                  <InstallPrompt />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route element={<Layout />}>
                        {/* Public routes */}
                        <Route path="/login" element={<Login />} />
                        <Route path="/auth/verify" element={<VerifyAuth />} />
                        <Route path="/auth/confirm-device" element={<ConfirmDevice />} />
                        <Route path="/auth/sso/callback" element={<SsoCallback />} />
                        <Route path="/auth/mfa" element={<MfaChallenge />} />
                        <Route path="/contratar" element={<SignupPage />} />
                        <Route path="/contratar/gracias" element={<SignupThanks />} />
                        <Route path="/auth/mfa-setup" element={<PrivateRoute><MfaSetup /></PrivateRoute>} />
                        <Route path="/verify" element={<Verify />} />
                        <Route path="/verify/:certId" element={<Verify />} />
                        <Route path="/onboarding" element={<PrivateRoute><OnboardingWizard /></PrivateRoute>} />
                        
                        {/* Private routes */}
                        <Route path="/" element={<PrivateRoute><Home /></PrivateRoute>} />
                        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                        <Route path="/ranking" element={<PrivateRoute><Leaderboard /></PrivateRoute>} />
                        <Route path="/admin" element={<PrivateRoute><Admin /></PrivateRoute>}>
                          <Route index element={<AdminIndexRedirect />} />
                          <Route path="dashboard" element={<AdminDashboard />} />
                          <Route path="company" element={<AdminCompany />} />
                          <Route path="employees" element={<AdminEmployees />} />
                          <Route path="payroll" element={<AdminPayroll />} />
                          <Route path="reports" element={<AdminReports />} />
                          <Route path="plan" element={<AdminPlan />} />
                          <Route path="incentives" element={<AdminIncentives />} />
                          <Route path="compliance" element={<AdminCompliance />} />
                                                    <Route path="backups" element={<AdminBackups />} />
                          <Route path="support" element={<AdminSupport />} />
                        </Route>
                        <Route path="/advisor" element={<PrivateRoute><AiAdvisor /></PrivateRoute>} />
                        <Route path="/plan" element={<PrivateRoute><MyPlans /></PrivateRoute>} />
                        <Route path="/super/analytics" element={<PrivateRoute><SuperAnalytics /></PrivateRoute>} />
                        <Route path="/conduccion/:routeId" element={<PrivateRoute><ActiveTripDashboard /></PrivateRoute>} />
                        <Route path="*" element={<NotFound />} />
                      </Route>
                      
                      {/* Standalone routes (No Layout) */}
                      <Route path="/bus-kiosk" element={<PrivateRoute><BusKiosk /></PrivateRoute>} />
                      <Route path="/informe-kpi" element={<PrivateRoute><KpiReport /></PrivateRoute>} />
                      <Route path="/legal" element={<LegalPolicy />} />
                    </Routes>
                  </Suspense>
                </ErrorBoundary>
              </BrowserRouter>
            </TenantProvider>
          </UserProvider>
        </OfflineSyncProvider>
      </I18nProvider>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;

